import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * GlobalInteractions — mounts inside <Router> so it can read the current
 * location. Re-runs whenever the route changes so newly rendered elements
 * automatically get the effects applied.
 *
 * Effects:
 *   1. Card Tilt  — .glass-blade elements tilt toward the cursor in 3D
 *   2. Magnetic   — primary CTA buttons drift toward the cursor
 */
const GlobalInteractions = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const cleanups = [];

    // Small delay so the incoming page has finished rendering its DOM
    const timer = setTimeout(() => {
      /* ── 1. Card Tilt ─────────────────────────────────────── */
      const cards = document.querySelectorAll(".glass-blade");

      const onCardMove = (e) => {
        const card = e.currentTarget;
        const r    = card.getBoundingClientRect();
        const rx   = ((e.clientY - r.top  - r.height / 2) / (r.height / 2)) * -6;
        const ry   = ((e.clientX - r.left - r.width  / 2) / (r.width  / 2)) *  6;
        card.style.transition = "transform 0.08s ease";
        card.style.transform  =
          `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(6px)`;
      };

      const onCardLeave = (e) => {
        const card = e.currentTarget;
        card.style.transition = "transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)";
        card.style.transform  = "";
      };

      cards.forEach((card) => {
        card.addEventListener("mousemove",  onCardMove);
        card.addEventListener("mouseleave", onCardLeave);
      });

      cleanups.push(() => {
        cards.forEach((card) => {
          card.removeEventListener("mousemove",  onCardMove);
          card.removeEventListener("mouseleave", onCardLeave);
        });
      });

      /* ── 2. Magnetic CTAs ─────────────────────────────────── */
      const btns = document.querySelectorAll(
        ".cta-button.primary, .form-submit-btn, .step-next-btn, .scroll-cta-btn"
      );

      const onBtnMove = (e) => {
        const btn = e.currentTarget;
        if (btn.disabled) return;
        const r  = btn.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width  / 2);
        const dy = e.clientY - (r.top  + r.height / 2);
        btn.style.transition = "transform 0.12s ease";
        btn.style.transform  = `translate(${dx * 0.22}px, ${dy * 0.22}px)`;
      };

      const onBtnLeave = (e) => {
        const btn = e.currentTarget;
        btn.style.transition = "transform 0.45s cubic-bezier(0.23, 1, 0.32, 1)";
        btn.style.transform  = "";
      };

      btns.forEach((btn) => {
        btn.addEventListener("mousemove",  onBtnMove);
        btn.addEventListener("mouseleave", onBtnLeave);
      });

      cleanups.push(() => {
        btns.forEach((btn) => {
          btn.removeEventListener("mousemove",  onBtnMove);
          btn.removeEventListener("mouseleave", onBtnLeave);
        });
      });
    }, 200);

    return () => {
      clearTimeout(timer);
      cleanups.forEach((fn) => fn());
    };
  }, [pathname]);

  return null;
};

export default GlobalInteractions;
